import React, { useMemo } from 'react';
import { db } from '../../../firebase/config';
import { collectionGroup, query, where, getDocs } from 'firebase/firestore';
import { estadoEmision, motivo, submotivo, formaDespacho } from '../../../data/listasDesplegables';

/**
 * SectionRiesgo - Componente modular para los detalles del ramo, vigencias y Endoso 0 (Cauciones).
 * Actualizado: Autolimpieza de campos de expiración al cambiar a Póliza Nueva.
 */
const SectionRiesgo = ({ state, actions }) => {
    const { formData, dbMasters, mode, riskFlags, subriesgosDisponibles } = state;
    const { 
        handleChange, 
        handleRiesgoChange, 
        handleArrayChange, 
        addArrayItem, 
        removeArrayItem, 
        handleCurrencyChange, 
        handleRefMonedaChange, 
        handlePagoCheck, 
        setFormData 
    } = actions;

    // --- REGLA UNIFICADA: CUALQUIER FLUJO DE RENOVACIÓN EN EL FORMULARIO ---
    const isRenewalFlow = useMemo(() => {
        const motivoClean = String(formData.motivo || '').toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
        return mode === 'renewal' || !!formData.polizaExpirante || motivoClean.includes('RENOVACION');
    }, [formData.motivo, formData.polizaExpirante, mode]);

    const isCambioCompania = useMemo(() => {
        const subOpClean = String(formData.subOperacion || '').toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
        return subOpClean.includes('CAMBIO DE COMPANIA');
    }, [formData.subOperacion]);

    // --- AUTO-BUSQUEDA AUTOMÁTICA DE COMPAÑÍA EXPIRANTE ---
    const handlePolizaExpiranteBlur = async (e) => {
        const polizaNum = e.target.value.trim().toUpperCase();
        if (!polizaNum) return;

        try {
            const q = query(collectionGroup(db, 'polizas'), where('nPoliza', '==', polizaNum));
            const snap = await getDocs(q);
            if (!snap.empty) {
                const polizaViejaData = snap.docs[0].data();
                if (polizaViejaData.compania) {
                    setFormData(prev => ({
                        ...prev,
                        companiaExpirante: polizaViejaData.compania
                    }));
                }
            }
        } catch (error) {
            console.warn("No se pudo autocompletar la compañía expirante:", error);
        }
    };

    // --- FILTRADO DINÁMICO DE SUB-OPERACIÓN SEGÚN TIPO DE OPERACIÓN ---
    const submotivoFiltrado = useMemo(() => {
        const tipoOp = String(formData.motivo || '').toUpperCase().trim();

        if (tipoOp.includes('POLIZA NUEVA') || tipoOp.includes('PÓLIZA NUEVA') || tipoOp === 'NUEVA') {
            return submotivo.filter(sm => [
                'Emisión Nueva', 
                'Emisión Fuera de Término', 
                'Emisión por Reemplazo',
                'Cambio de PAS'
            ].includes(sm));
        }

        if (tipoOp.includes('RENOVACION') || tipoOp.includes('RENOVACIÓN')) {
            return submotivo.filter(sm => [
                'Continuidad de Cobertura', 
                'Cambio de Compañia',
                'Cambio de Compañía'
            ].includes(sm));
        }

        return submotivo;
    }, [formData.motivo]);

    // --- ORDENAMIENTO EN MEMORIA ---
    const sortedRiesgos = useMemo(() => {
        return [...(dbMasters?.riesgos || [])]
            .filter(c => c.grupo === 'PATRIMONIALES')
            .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || ''));
    }, [dbMasters?.riesgos]);

    const sortedSubriesgos = useMemo(() => {
        const list = [...(subriesgosDisponibles || [])];
        if (formData.subriesgo && !list.includes(formData.subriesgo)) {
            list.push(formData.subriesgo);
        }
        return list.sort((a, b) => (a || '').localeCompare(b || ''));
    }, [subriesgosDisponibles, formData.subriesgo]);

    const sortedCompanias = useMemo(() => {
        return [...(dbMasters?.companias || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.companias]);

    const sortedColaboradores = useMemo(() => {
        return [...(dbMasters?.colaboradores || [])].sort((a, b) => 
            (a.nombre || '').localeCompare(b.nombre || '')
        );
    }, [dbMasters?.colaboradores]);

    // Renders dinámicos para campos array
    const renderDynamicMultiField = (field, label, colorClass, placeholder) => {
        const items = (formData[field] && formData[field].length > 0) ? formData[field] : [''];
        return (
            <div className="col-span-full mt-6 mb-4 p-4 bg-slate-50/30 rounded-xl border border-slate-100 animate-fade-in">
                <label className={`text-[15px] font-bold mb-2 block ${colorClass}`}>{label}</label>
                <div className="flex flex-wrap gap-2 items-center">
                    {items.map((item, idx) => (
                        <div key={idx} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 transition-all focus-within:border-blue-300">
                            <input 
                                type="text" 
                                value={item || ''} 
                                onChange={(e) => handleArrayChange(field, idx, e.target.value)} 
                                className="outline-none bg-transparent font-semibold text-slate-600 w-full min-w-[280px] text-sm placeholder:text-slate-300" 
                                placeholder={placeholder} 
                            />
                            {items.length > 1 && (
                                <button type="button" onClick={() => removeArrayItem(field, idx)} className="text-slate-300 hover:text-red-400 transition-colors">
                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    ))}
                    <button type="button" onClick={() => addArrayItem(field)} className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all duration-300 border border-blue-100 shadow-sm">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 6v12M6 12h12" />
                        </svg>
                    </button>
                </div>
            </div>
        );
    };

    return (
        <fieldset className="border border-gray-200 rounded-xl p-6 bg-white shadow-sm text-left transition-all duration-300">
            <legend className="px-3 text-lg font-bold text-gray-700 tracking-tight">Detalles del Riesgo</legend>
            
            {/* FILA 1: CARGA, RIESGO, SUBRIESGO */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 pt-2">
                <div>
                    <label className="label-form">Fecha de Carga</label>
                    <input type="date" value={formData.fechaCarga || ''} readOnly className="input-round bg-gray-50 text-gray-400 cursor-not-allowed border-slate-100" />
                </div>
                <div>
                    <label className="label-form">Riesgo *</label>
                    <select 
                        value={formData.riesgo || ''} 
                        onChange={(e) => handleRiesgoChange(e.target.value)} 
                        className="input-round font-bold border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" 
                        required
                    >
                        <option value="">Seleccione...</option>
                        {sortedRiesgos.map(r => (
                            <option key={r.id} value={r.nombre}>
                                {r.nombre}
                            </option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="label-form">Subriesgo *</label>
                    <select 
                        name="subriesgo" 
                        value={formData.subriesgo || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" 
                        disabled={sortedSubriesgos.length === 0}
                        required
                    >
                        <option value="">Seleccione...</option>
                        {sortedSubriesgos.map((s, i) => (
                            <option key={i} value={s}>
                                {s}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* FILA 2: TIPO OPERACIÓN, SUB-OPERACIÓN, INICIO VIGENCIA, FIN VIGENCIA */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 pt-6">
                <div>
                    <label className="label-form">Tipo Operación *</label>
                    <select 
                        name="motivo" 
                        value={formData.motivo || ''} 
                        onChange={(e) => {
                            const nuevoMotivo = e.target.value;
                            const esRenovacion = String(nuevoMotivo || '').toUpperCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").includes('RENOVACION');

                            setFormData(prev => ({
                                ...prev,
                                motivo: nuevoMotivo,
                                subOperacion: '', // Resetea sub-operación al cambiar motivo
                                ...(!esRenovacion ? {
                                    polizaExpirante: '',
                                    companiaExpirante: '',
                                    quienRenovo: ''
                                } : {})
                            }));
                        }} 
                        className={`input-round font-bold border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100 ${mode === 'renewal' ? 'bg-indigo-50 text-indigo-700 border-indigo-200' : ''}`} 
                        disabled={mode === 'renewal'}
                        required
                    >
                        <option value="">Seleccione...</option>
                        {motivo.filter(m => !['Póliza Histórica', 'Cambio de PAS', 'Cuenta Bancaria', 'Emisión por Reemplazo'].includes(m)).map((m,i)=>(
                            <option key={i} value={m}>{m}</option>
                        ))}
                    </select>
                    {mode === 'renewal' && <p className="text-[10px] font-bold text-indigo-600 mt-1 uppercase italic tracking-tighter">Modo Renovación Activo</p>}
                </div>

                {/* SUB-OPERACIÓN FILTRADA DINÁMICAMENTE */}
                <div>
                    <label className="label-form">Sub-Operación *</label>
                    <select 
                        name="subOperacion" 
                        value={formData.subOperacion || ''} 
                        onChange={handleChange} 
                        className="input-round border-slate-200 font-bold focus:border-blue-400 focus:ring-1 focus:ring-blue-100"
                        required
                    >
                        <option value="">Seleccione...</option>
                        {submotivoFiltrado.map((sm, i) => (
                            <option key={i} value={sm}>{sm}</option>
                        ))}
                    </select>
                </div>

                <div>
                    <label className="label-form text-blue-700 font-bold text-[10px] uppercase tracking-wide">Inicio Vigencia *</label>
                    <input name="inicioVigencia" type="date" value={formData.inicioVigencia || ''} onChange={handleChange} className="input-round border-blue-200 font-bold text-blue-700 bg-blue-50/10 focus:bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-100" required />
                </div>
                <div>
                    <label className={`label-form font-bold text-[10px] uppercase tracking-wide ${riskFlags.esCaucionEspecial ? 'text-slate-300' : 'text-blue-700'}`}>Fin Vigencia *</label>
                    <input 
                        name="finVigencia" 
                        type="date" 
                        value={formData.finVigencia || ''} 
                        onChange={handleChange} 
                        className={`input-round font-bold ${riskFlags.esCaucionEspecial ? 'bg-slate-50 border-slate-100 cursor-not-allowed text-slate-300' : 'border-blue-200 text-blue-700 bg-blue-50/10 focus:bg-white focus:border-blue-400 focus:ring-1 focus:ring-blue-100'}`} 
                        disabled={riskFlags.esCaucionEspecial} 
                        required={!riskFlags.esCaucionEspecial} 
                    />
                    {riskFlags.esCaucionEspecial && <p className="text-[8px] font-black text-amber-500 uppercase mt-1 italic tracking-tighter pl-1">Calculado por Endoso 0</p>}
                </div>
            </div>

            {/* FILA 3: ESTADO PÓLIZA, N° PÓLIZA, PÓLIZA EXPIRANTE, COMPAÑÍA EXPIRANTE Y QUIÉN RENOVÓ */}
            <div className={`grid grid-cols-1 ${isRenewalFlow && mode !== 'renewal' ? 'md:grid-cols-3 lg:grid-cols-5' : (isRenewalFlow ? 'md:grid-cols-3' : 'md:grid-cols-2')} gap-6 pt-6`}>
                <div>
                    <label className="label-form">Estado Póliza *</label>
                    <select name="estadoEmision" value={formData.estadoEmision || ''} onChange={handleChange} className="input-round border-slate-200 font-bold text-slate-700 bg-slate-50/50 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" required>
                        <option value="">Seleccione...</option>
                        {estadoEmision.map((e,i)=><option key={i} value={e}>{e}</option>)}
                    </select>
                </div>
                <div>
                    <label className="label-form">N° de Póliza *</label>
                    <div className="relative">
                        <input 
                            name="nPoliza" 
                            type="text" 
                            value={formData.nPoliza || ''} 
                            onChange={(e) => {
                                const isFlex = ['PENDIENTES DE EMISIÓN', 'PENDIENTE DE EMISIÓN', 'PENDIENTE DE INSPECCIÓN', 'PROPUESTA INGRESADA'].includes(formData.estadoEmision);
                                const val = isFlex ? e.target.value.toUpperCase() : e.target.value.replace(/\D/g, '');
                                setFormData(prev => ({...prev, nPoliza: val}));
                            }} 
                            className="input-round font-mono font-bold text-blue-600 uppercase border-slate-200 focus:border-blue-400 focus:ring-1 focus:ring-blue-100" 
                            required 
                        />
                        {formData.polizaExpirante && (
                            <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5 animate-fade-in bg-white pl-2">
                                <span className="h-4 w-px bg-slate-200"></span>
                                <span className="text-[9px] font-black text-slate-400 uppercase tracking-tighter italic">EXP: #{formData.polizaExpirante}</span>
                            </div>
                        )}
                    </div>
                </div>

                {/* CAMPO ADICIONAL 1: PÓLIZA EXPIRANTE EN RENOVACIÓN */}
                {isRenewalFlow && mode !== 'renewal' && (
                    <div className="animate-fade-in">
                        <label className="label-form text-indigo-700 font-bold text-[10px]">Póliza Expirante *</label>
                        <input 
                            type="text" 
                            name="polizaExpirante" 
                            value={formData.polizaExpirante || ''} 
                            onChange={handleChange} 
                            onBlur={handlePolizaExpiranteBlur}
                            placeholder="Ej: 12345678" 
                            className="input-round font-mono font-bold text-indigo-800 border-indigo-300 bg-indigo-50/20 focus:bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 uppercase" 
                            required 
                        />
                    </div>
                )}

                {/* CAMPO ADICIONAL 2: COMPAÑÍA EXPIRANTE (SE AUTOCOMPLETA O PUEDE ELEGIRSE) */}
                {isRenewalFlow && mode !== 'renewal' && (
                    <div className="animate-fade-in">
                        <label className="label-form text-indigo-700 font-bold text-[10px]">Compañía Expirante *</label>
                        <select 
                            name="companiaExpirante" 
                            value={formData.companiaExpirante || ''} 
                            onChange={handleChange} 
                            className="input-round border-indigo-300 bg-indigo-50/20 font-bold text-indigo-800 focus:bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all"
                            required
                        >
                            <option value="">Seleccione Compañia...</option>
                            {sortedCompanias.map(c => (
                                <option key={c.id} value={c.nombre}>
                                    {c.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                )}

                {/* CAMPO OBLIGATORIO: QUIÉN REALIZÓ LA RENOVACIÓN */}
                {isRenewalFlow && (
                    <div className="animate-fade-in">
                        <label className="label-form text-indigo-700 font-bold text-[10px]">¿Quién Renovó? *</label>
                        <select 
                            name="quienRenovo" 
                            value={formData.quienRenovo || ''} 
                            onChange={handleChange} 
                            className="input-round border-indigo-300 bg-indigo-50/30 font-bold text-indigo-800 focus:bg-white focus:border-indigo-500 focus:ring-1 focus:ring-indigo-100 transition-all"
                            required={isRenewalFlow}
                        >
                            <option value="">Seleccione Colaborador...</option>
                            {sortedColaboradores.map(c => (
                                <option key={c.id} value={c.nombre}>
                                    {c.nombre}
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            {/* CAMPOS DINÁMICOS DEPENDIENDO DEL RAMO */}
            <div className="col-span-full">
                {riskFlags.esVehiculo && renderDynamicMultiField('patentes', 'Patente / Dominio', 'text-red-500', 'Ej: ABC-123')}
                {riskFlags.esAP && renderDynamicMultiField('actividades', 'Actividades de Accidentes Personales', 'text-blue-500', 'Ej: Albañilería, Plomería...')}
                {riskFlags.esMalaPraxis && renderDynamicMultiField('especialidades', 'Especialidades Médicas', 'text-blue-500', 'Ej: Pediatría, Cirugía...')}
                
                {/* Bloque de Ubicaciones del Riesgo */}
                {riskFlags.requiereUbicacion && (
                    <div className="col-span-full mt-6 mb-4 p-4 bg-slate-50/30 rounded-xl border border-slate-100 animate-fade-in">
                        <label className="text-[15px] font-bold mb-2 block text-orange-500">Ubicaciones del Riesgo</label>
                        <div className="flex flex-col gap-3 items-start">
                            {(formData.ubicacionesRiesgo?.length > 0 ? formData.ubicacionesRiesgo : [{ direccion: '', m2: '' }]).map((item, idx) => {
                                const direccionVal = typeof item === 'string' ? item : (item?.direccion || '');
                                const m2Val = typeof item === 'object' ? (item?.m2 || '') : '';
                                const requiereM2 = !formData.riesgo?.toUpperCase().includes('RESPONSABILIDAD CIVIL');

                                return (
                                    <div key={idx} className="flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-slate-200 transition-all focus-within:border-blue-300 shadow-sm">
                                        <input 
                                            type="text" 
                                            value={direccionVal} 
                                            onChange={(e) => {
                                                setFormData(prev => {
                                                    const newArr = [...(prev.ubicacionesRiesgo || [])];
                                                    const current = newArr[idx] || {};
                                                    const baseObj = typeof current === 'string' ? { direccion: current, m2: '' } : current;
                                                    newArr[idx] = { ...baseObj, direccion: e.target.value };
                                                    return { ...prev, ubicacionesRiesgo: newArr };
                                                });
                                            }} 
                                            className="outline-none bg-transparent font-semibold text-slate-600 w-full min-w-[280px] text-sm placeholder:text-slate-300" 
                                            placeholder="Ej: Av. Rivadavia 1234..." 
                                            required
                                        />
                                        
                                        {requiereM2 && (
                                            <>
                                                <div className="h-6 w-px bg-slate-200 mx-1"></div>
                                                <input 
                                                    type="number" 
                                                    value={m2Val} 
                                                    onChange={(e) => {
                                                        setFormData(prev => {
                                                            const newArr = [...(prev.ubicacionesRiesgo || [])];
                                                            const current = newArr[idx] || {};
                                                            const baseObj = typeof current === 'string' ? { direccion: current, m2: '' } : current;
                                                            newArr[idx] = { ...baseObj, m2: e.target.value };
                                                            return { ...prev, ubicacionesRiesgo: newArr };
                                                        });
                                                    }} 
                                                    className="outline-none bg-transparent font-semibold text-slate-600 w-20 text-sm text-center placeholder:text-slate-300" 
                                                    placeholder="m²" 
                                                    required
                                                />
                                            </>
                                        )}

                                        {((formData.ubicacionesRiesgo?.length || 1) > 1) && (
                                            <button 
                                                type="button" 
                                                onClick={() => {
                                                    setFormData(prev => {
                                                        const newArr = [...(prev.ubicacionesRiesgo || [])];
                                                        newArr.splice(idx, 1);
                                                        return { 
                                                            ...prev, 
                                                            ubicacionesRiesgo: newArr.length === 0 ? [{ direccion: '', m2: '' }] : newArr 
                                                        };
                                                    });
                                                }} 
                                                className="text-slate-300 hover:text-red-400 ml-2 transition-colors"
                                            >
                                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                );
                            })}
                            <button 
                                type="button" 
                                onClick={() => {
                                    setFormData(prev => ({
                                        ...prev,
                                        ubicacionesRiesgo: [...(prev.ubicacionesRiesgo || []), { direccion: '', m2: '' }]
                                    }));
                                }} 
                                className="w-7 h-7 rounded-full bg-blue-50 text-blue-600 flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all duration-300 border border-blue-100 mt-2 shadow-sm"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M12 6v12M6 12h12" />
                                </svg>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* SECCIÓN DE REFACTURACIÓN - ENDOSO 0 */}
            {riskFlags.showEndoso0 && (
                <div className="mt-8 p-6 bg-amber-50/5 rounded-2xl border border-amber-100 animate-fade-in text-left">
                    <div className="flex justify-between items-center mb-6">
                        <h3 className="text-sm font-black text-amber-800 uppercase flex items-center gap-2 tracking-tight">
                            <svg className="w-4 h-4 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Detalle de Refacturación - ENDOSO 0
                        </h3>
                        <div className="flex items-center gap-2 select-none">
                             <span className="text-[9px] font-bold text-amber-700 uppercase">Estado:</span>
                             <span className={`px-2 py-0.5 rounded text-[10px] font-black uppercase ${formData.ref_pagoEfectuado ? 'bg-emerald-500 text-white shadow-sm' : 'bg-amber-100 text-amber-800 border border-amber-200'}`}>
                                {formData.ref_estadoGestion || 'Revisión'}
                             </span>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div>
                            <label className="label-sub">Número de Factura</label>
                            <input 
                                name="ref_numeroFactura" 
                                type="text" 
                                value={formData.ref_numeroFactura || ''} 
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setFormData(prev => ({ ...prev, ref_numeroFactura: val }));
                                }} 
                                className="input-round border-amber-100 bg-white/50 font-mono font-bold" 
                                placeholder="Solo números"
                            />
                        </div>
                        <div>
                            <label className="label-sub">Inicio de Vigencia</label>
                            <input name="ref_inicioVigencia" type="date" value={formData.ref_inicioVigencia || ''} onChange={handleChange} className="input-round border-amber-100 bg-white/50 focus:border-amber-400 focus:ring-1 focus:ring-amber-100" />
                        </div>
                        <div>
                            <label className="label-sub">Fin de Vigencia</label>
                            <input name="ref_finVigencia" type="date" value={formData.ref_finVigencia || ''} onChange={handleChange} className="input-round border-amber-100 bg-white/50 focus:border-amber-400 focus:ring-1 focus:ring-amber-100" />
                        </div>
                        
                        <div>
                            <label className="label-sub">Prima de la Caución</label>
                            <div className="currency-container-premium !border-amber-100 !h-[46px]">
                                <div className="currency-addon !bg-amber-50/50">
                                    <select 
                                        value={formData.ref_moneda === 'USD' ? 'USD U$S' : 'ARS $'} 
                                        onChange={(e) => handleRefMonedaChange(e.target.value)} 
                                        className="currency-select-premium !text-amber-800"
                                    >
                                        <option value="ARS $">ARS $</option>
                                        <option value="USD U$S">USD U$S</option>
                                    </select>
                                </div>
                                <input type="text" name="ref_prima" value={formData.ref_prima || ''} onChange={(e) => handleCurrencyChange(e, 'ref_prima')} className="currency-input-premium !text-amber-900 !text-sm" placeholder="0,00" />
                            </div>
                        </div>
                        <div>
                            <label className="label-sub">Importe de la Caución</label>
                            <div className="currency-container-premium !border-amber-100 !h-[46px]">
                                <div className="currency-addon !bg-amber-50/50">
                                    <span className="currency-symbol-premium !text-amber-800">{formData.ref_moneda === 'USD' ? 'USD' : '$'}</span>
                                </div>
                                <input type="text" name="ref_primaEmitida" value={formData.ref_primaEmitida || ''} onChange={(e) => handleCurrencyChange(e, 'ref_primaEmitida')} className="currency-input-premium !text-amber-900 !text-sm" placeholder="0,00" />
                            </div>
                        </div>
                        <div>
                            <label className="label-sub">Forma de Despacho</label>
                            <select name="ref_formaDespacho" value={formData.ref_formaDespacho || ''} onChange={handleChange} className="input-round border-amber-100 bg-white/50">
                                <option value="">Seleccione...</option>
                                {formaDespacho.map(f => <option key={f} value={f}>{f}</option>)}
                            </select>
                        </div>
                        <div>
                            <label className="label-sub">Fecha de Despacho</label>
                            <input name="ref_fechaDespacho" type="date" value={formData.ref_fechaDespacho || ''} onChange={handleChange} className="input-round border-amber-100 bg-white/50" />
                        </div>
                    </div>

                    <div className="mt-8 pt-6 border-t border-amber-100 flex flex-col gap-4">
                        <div className={`flex items-center justify-between p-4 rounded-xl border transition-all duration-300 ${formData.ref_pagoEfectuado ? 'bg-emerald-50/40 border-emerald-100' : 'bg-white/40 border-slate-100'}`}>
                            <div className="flex items-center gap-3 select-none">
                                <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors duration-300 ${formData.ref_pagoEfectuado ? 'bg-emerald-500 text-white' : 'bg-slate-100 text-slate-300'}`}>
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>
                                <p className={`text-xs font-bold uppercase tracking-tight ${formData.ref_pagoEfectuado ? 'text-emerald-900' : 'text-slate-600'}`}>¿Pago Efectuado?</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer scale-110 select-none">
                                <input 
                                    type="checkbox" 
                                    name="ref_pagoEfectuado" 
                                    checked={formData.ref_pagoEfectuado || false} 
                                    onChange={(e) => handlePagoCheck(e.target.checked)} 
                                    className="sr-only peer" 
                                />
                                <div className="w-10 h-5 bg-slate-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-emerald-500"></div>
                            </label>
                        </div>

                        {formData.ref_pagoEfectuado && (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-fade-in p-2">
                                <div>
                                    <label className="label-sub text-emerald-700">¿Quién Cobró?</label>
                                    <select name="ref_quienCobro" value={formData.ref_quienCobro || ''} onChange={handleChange} className="input-round border-emerald-100 bg-white">
                                        <option value="">Seleccione Colaborador...</option>
                                        {sortedColaboradores.map(c => (
                                            <option key={c.id} value={c.nombre}>
                                                {c.nombre}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div>
                                    <label className="label-sub text-emerald-700">Fecha de Cobro</label>
                                    <input name="ref_fechaCobro" type="date" value={formData.ref_fechaCobro || ''} onChange={handleChange} className="input-round border-emerald-100 bg-white" />
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <style>{`
                .label-sub { display: block; font-size: 14px; font-weight: 700; color: #b45309; margin-bottom: 0.25rem; }
                .currency-container-premium { display: flex; align-items: center; border: 1px solid #d1d5db; border-radius: 0.75rem; overflow: hidden; height: 50px; background-color: #fff; transition: all 0.2s; }
                .currency-container-premium:focus-within { border-color: #2563eb; }
                .currency-addon { background-color: #f9fafb; padding: 0 0.75rem; height: 100%; display: flex; align-items: center; min-width: 85px; justify-content: center; }
                .currency-select-premium { background: transparent; font-weight: 800; color: #374151; outline: none; cursor: pointer; text-transform: uppercase; font-size: 0.7rem; border: none; }
                .currency-symbol-premium { font-weight: 800; color: #374151; font-size: 0.7rem; text-transform: uppercase; }
                .currency-input-premium { flex-grow: 1; padding: 0 1rem; height: 100%; outline: none; background: transparent; font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace; font-weight: 700; font-size: 1rem; color: #111827; width: 100%; }
                .animate-fade-in { animation: fadeIn 0.3s ease-out; }
                @keyframes fadeIn { from { opacity: 0; transform: translateY(5px); } to { opacity: 1; transform: translateY(0); } }
            `}</style>
        </fieldset>
    );
};

export default SectionRiesgo;